import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  MapPin, Navigation, Compass, Building2, ArrowRight, ArrowLeft,
  CheckCircle, User, Briefcase, Phone, Mail, Globe, Clock,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = ['Contact', 'Your Route', 'Assessment', 'Vision & Goals', 'Engagement'];

const ConsultingIntake = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    // Step 1 — Contact
    fullName: '', preferredName: '', phone: '', email: '', cityState: '',
    bestTime: '', businessName: '', businessType: '', industry: '', businessWebsite: '',
    howFound: '', referredBy: '',
    // Step 2 — Route
    route: '', // builder | personal | corporate
    whyAce: '',
    // Step 3 — Assessment (varies by route)
    // Builder (A) fields
    hasEntity: '', einEstablished: '', hasBankAccount: '', yearsInBusiness: '',
    businessCreditStatus: '', personalCreditRange: '', availableCapital: '',
    fleetAccess: '',
    // Personal (B) fields
    transportMode: [], currentVehicle: '', vehicleOwnership: '', monthlyPayment: '',
    vehicleSatisfaction: '', painPoints: [],
    // Corporate (C) fields
    companyName: '', companyTitle: '', employeeCount: '', monthlyTransportSpend: '',
    currentTransportSetup: [],
    // Step 4 — Vision & Goals
    // Builder
    businessVision: '', serviceTypes: [], operationalRole: '', targetMarket: '', targetCustomer: '',
    // Personal
    lifestyleVision: '', priorityGoals: [], dreamVehicle: '', acquisitionMethod: '', monthlyBudget: '',
    // Corporate
    transportNeeds: [], upcomingEvents: '', passengerCount: '', eventDates: '', eventBudget: '',
    // Step 5 — Engagement
    timeline: '', supportType: '', membershipInterest: '', investmentRange: '', finalNotes: '',
  });

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const toggleArray = (key, val) => {
    setForm(p => {
      const arr = p[key] || [];
      return { ...p, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };

  const handleSubmit = async () => {
    if (!form.fullName || !form.phone || !form.email || !form.route) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/api/consulting/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, formType: 'client_profile', submittedAt: new Date().toISOString() })
      });
      setSubmitted(true);
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="intake-success">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Profile Received.</h2>
          <p className="text-gray-400 text-sm mb-6">Your ACE consultant will review your submission personally and reach out within 24 hours to schedule your Discovery Session.</p>
          <p className="text-xs text-gray-600 mb-8">Next Step: Paid Discovery Session — 60 min with Your Auto Concierge</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/driving')} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs">Back to Consulting</Button>
            <a href="tel:6788463009" className="inline-flex items-center h-9 px-4 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-bold">
              <Phone className="h-3.5 w-3.5 mr-1.5" /> (678) 846-3009
            </a>
          </div>
        </div>
      </div>
    );
  }

  const routeLabels = { builder: "The Builder's Route", personal: 'The Personal Lane', corporate: 'The Executive Corridor' };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="consulting-intake-page">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-950">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <p className="text-red-500 text-[10px] font-bold tracking-[0.3em] uppercase mb-1">ACE Auto Lifestyle Consulting</p>
          <h1 className="text-xl sm:text-2xl font-bold">Client Profile & Intake</h1>
          <p className="text-sm text-gray-500 mt-1">Complete this profile so we can understand exactly where you are and where you're trying to go.</p>
        </div>
      </div>

      {/* Progress */}
      <div className="border-b border-gray-900 bg-gray-950/50">
        <div className="max-w-3xl mx-auto px-6 py-3">
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-1.5 ${step > i + 1 ? 'text-green-500' : step === i + 1 ? 'text-white' : 'text-gray-700'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    step > i + 1 ? 'bg-green-500/20 text-green-500' : step === i + 1 ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-600'
                  }`}>{step > i + 1 ? <CheckCircle className="h-3.5 w-3.5" /> : i + 1}</div>
                  <span className="text-[10px] font-semibold hidden sm:inline">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${step > i + 1 ? 'bg-green-500/30' : 'bg-gray-800'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Step 1 — Contact */}
        {step === 1 && (
          <div className="space-y-8" data-testid="step-contact">
            <div>
              <h2 className="text-lg font-bold mb-1">Who Are You?</h2>
              <p className="text-sm text-gray-500">This creates your ACE client file.</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">Personal Information</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *" value={form.fullName} onChange={v => set('fullName', v)} testId="field-fullName" />
                <Field label="Preferred Name" value={form.preferredName} onChange={v => set('preferredName', v)} />
                <Field label="Phone *" value={form.phone} onChange={v => set('phone', v)} testId="field-phone" />
                <Field label="Email *" value={form.email} onChange={v => set('email', v)} type="email" testId="field-email" />
                <Field label="City / State" value={form.cityState} onChange={v => set('cityState', v)} />
                <SelectField label="Best Time to Reach" value={form.bestTime} onChange={v => set('bestTime', v)}
                  options={['Morning (8am–12pm)', 'Afternoon (12pm–5pm)', 'Evening (5pm–9pm)', 'Anytime']} />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">Business Information (if applicable)</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Business / Brand Name" value={form.businessName} onChange={v => set('businessName', v)} />
                <SelectField label="Business Type" value={form.businessType} onChange={v => set('businessType', v)}
                  options={['LLC', 'Corporation (C or S)', 'Sole Proprietor', 'Partnership', 'No entity yet']} />
                <Field label="Industry / Sector" value={form.industry} onChange={v => set('industry', v)} />
                <Field label="Business Website" value={form.businessWebsite} onChange={v => set('businessWebsite', v)} />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">How You Found ACE</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <SelectField label="How did you hear about us?" value={form.howFound} onChange={v => set('howFound', v)}
                  options={['Direct text / call', 'Instagram', 'Facebook', 'Google Search', 'Referral', 'Driven or Driving Podcast', 'YourAutoConcierge.com', 'Other']} />
                <Field label="Referred by (if applicable)" value={form.referredBy} onChange={v => set('referredBy', v)} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Route Selection */}
        {step === 2 && (
          <div className="space-y-8" data-testid="step-route">
            <div>
              <h2 className="text-lg font-bold mb-1">What Brings You to ACE?</h2>
              <p className="text-sm text-gray-500">Select the route that shapes every question that follows.</p>
            </div>
            <div className="grid gap-4">
              <RouteOption id="builder" icon={Compass} label="The Builder's Route" sub="Investor / Entrepreneur"
                desc="I want to build a business in the auto and transportation space."
                color="from-amber-500 to-orange-600" selected={form.route === 'builder'} onClick={() => set('route', 'builder')} />
              <RouteOption id="personal" icon={Navigation} label="The Personal Lane" sub="Individual / Personal"
                desc="I want to upgrade, optimize, or transform how I move through the world."
                color="from-sky-500 to-blue-600" selected={form.route === 'personal'} onClick={() => set('route', 'personal')} />
              <RouteOption id="corporate" icon={Building2} label="The Executive Corridor" sub="Corporate / Executive"
                desc="I need auto lifestyle solutions for my team, executives, or events."
                color="from-emerald-500 to-green-600" selected={form.route === 'corporate'} onClick={() => set('route', 'corporate')} />
            </div>
            <div>
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">In your own words — what brought you to ACE?</p>
              <Textarea value={form.whyAce} onChange={e => set('whyAce', e.target.value)} className="bg-gray-900 border-gray-700 text-white min-h-[100px]" placeholder="What problem are you solving or what goal are you working toward?" data-testid="field-whyAce" />
            </div>
          </div>
        )}

        {/* Step 3 — Assessment */}
        {step === 3 && (
          <div className="space-y-8" data-testid="step-assessment">
            <div>
              <h2 className="text-lg font-bold mb-1">Where Are You Now?</h2>
              <p className="text-sm text-gray-500">No judgment — just context so we build the right roadmap.</p>
            </div>

            {form.route === 'builder' && (
              <>
                <Section title="Business Structure">
                  <SelectField label="Registered business entity?" value={form.hasEntity} onChange={v => set('hasEntity', v)}
                    options={['Yes — LLC', 'Yes — Corporation', 'Yes — Sole Proprietor', 'No entity yet']} />
                  <SelectField label="EIN established?" value={form.einEstablished} onChange={v => set('einEstablished', v)} options={['Yes', 'No', 'Not sure']} />
                  <SelectField label="Business bank account?" value={form.hasBankAccount} onChange={v => set('hasBankAccount', v)} options={['Yes', 'No']} />
                  <SelectField label="Years in business" value={form.yearsInBusiness} onChange={v => set('yearsInBusiness', v)}
                    options={['Brand new — 0', 'Less than 1 year', '1–3 years', '3–5 years', '5+ years']} />
                </Section>
                <Section title="Business Credit Status">
                  <RadioCards value={form.businessCreditStatus} onChange={v => set('businessCreditStatus', v)} options={[
                    'No credit profile established yet',
                    'Just starting to build — a few accounts open',
                    'Some tradelines established, building momentum',
                    'Strong business credit profile in place',
                  ]} />
                </Section>
                <Section title="Personal Credit & Capital">
                  <SelectField label="Personal credit score range" value={form.personalCreditRange} onChange={v => set('personalCreditRange', v)}
                    options={['Below 580', '580–649', '650–699', '700–749', '750+', 'Not sure']} />
                  <SelectField label="Available capital to deploy now" value={form.availableCapital} onChange={v => set('availableCapital', v)}
                    options={['Under $10,000', '$10,000–$25,000', '$25,000–$50,000', '$50,000–$100,000', '$100,000+', 'Seeking funding']} />
                </Section>
                <Section title="Fleet & Vehicle Access">
                  <RadioCards value={form.fleetAccess} onChange={v => set('fleetAccess', v)} options={[
                    'No vehicles', 'Personal vehicle only', '1–2 commercial vehicles', '3–5 vehicles', '6 or more vehicles',
                  ]} />
                </Section>
              </>
            )}

            {form.route === 'personal' && (
              <>
                <Section title="How You Currently Move">
                  <CheckboxGroup selected={form.transportMode} onToggle={v => toggleArray('transportMode', v)} options={[
                    'I drive myself daily', 'Mix of self-drive and rideshare', 'Primarily Uber / Lyft',
                    'I have a personal driver', 'Company provides transport',
                  ]} />
                </Section>
                <Section title="Current Vehicle">
                  <Field label="Year / Make / Model" value={form.currentVehicle} onChange={v => set('currentVehicle', v)} />
                  <SelectField label="Owned, Leased, or Financed?" value={form.vehicleOwnership} onChange={v => set('vehicleOwnership', v)}
                    options={['Owned outright', 'Financed / auto loan', 'Leased', 'Company vehicle', 'No personal vehicle']} />
                  <Field label="Monthly payment (if any)" value={form.monthlyPayment} onChange={v => set('monthlyPayment', v)} />
                  <SelectField label="Vehicle satisfaction (1–5)" value={form.vehicleSatisfaction} onChange={v => set('vehicleSatisfaction', v)}
                    options={['1 — Very unhappy', '2 — Not satisfied', '3 — It works', '4 — Pretty happy', '5 — Love it']} />
                </Section>
                <Section title="Current Pain Points">
                  <CheckboxGroup selected={form.painPoints} onToggle={v => toggleArray('painPoints', v)} options={[
                    'Overpaying for my vehicle', 'Wrong vehicle for my lifestyle', 'Spending too much time driving',
                    'Reliability / maintenance issues', "Image doesn't match my brand", 'No access to luxury options',
                    'Lack of a personal driver', 'Poor rideshare experiences',
                  ]} />
                </Section>
              </>
            )}

            {form.route === 'corporate' && (
              <>
                <Section title="Organization Details">
                  <Field label="Company Name *" value={form.companyName} onChange={v => set('companyName', v)} />
                  <Field label="Your Title / Role" value={form.companyTitle} onChange={v => set('companyTitle', v)} />
                  <SelectField label="Number of Employees" value={form.employeeCount} onChange={v => set('employeeCount', v)}
                    options={['1–10', '11–50', '51–200', '200+']} />
                  <SelectField label="Monthly transport spend" value={form.monthlyTransportSpend} onChange={v => set('monthlyTransportSpend', v)}
                    options={['Under $500', '$500–$2,000', '$2,000–$5,000', '$5,000–$15,000', '$15,000+']} />
                </Section>
                <Section title="Current Transportation Setup">
                  <CheckboxGroup selected={form.currentTransportSetup} onToggle={v => toggleArray('currentTransportSetup', v)} options={[
                    'Ad hoc rideshare (Uber/Lyft)', 'In-house vehicles / fleet', 'Outside transportation vendor',
                    'Employee reimbursement', 'No formal system in place',
                  ]} />
                </Section>
              </>
            )}
          </div>
        )}

        {/* Step 4 — Vision & Goals */}
        {step === 4 && (
          <div className="space-y-8" data-testid="step-vision">
            <div>
              <h2 className="text-lg font-bold mb-1">Where Are You Going?</h2>
              <p className="text-sm text-gray-500">Tell us about your vision and what success looks like.</p>
            </div>

            {form.route === 'builder' && (
              <>
                <Section title="Business Vision">
                  <Textarea value={form.businessVision} onChange={e => set('businessVision', e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white min-h-[100px]" placeholder="Describe the transportation business you want to build" />
                </Section>
                <Section title="Service Type Interest">
                  <CheckboxGroup selected={form.serviceTypes} onToggle={v => toggleArray('serviceTypes', v)} options={[
                    'Luxury Ground / Black Car', 'Rideshare / TNC', 'Airport Transfers',
                    'Event & Entertainment', 'Corporate Accounts', 'Fleet Rental / Self-Drive',
                    'Broker / Affiliate Model', 'ACCD Training / Certification',
                  ]} />
                </Section>
                <Section title="Operational Role">
                  <RadioCards value={form.operationalRole} onChange={v => set('operationalRole', v)} options={[
                    'Active operator — I\'ll be driving', 'Owner / manager — not driving',
                    'Passive investor', 'Still figuring this out',
                  ]} />
                </Section>
                <Section title="Target Market">
                  <Field label="Target city / market" value={form.targetMarket} onChange={v => set('targetMarket', v)} />
                  <Field label="Who are you serving?" value={form.targetCustomer} onChange={v => set('targetCustomer', v)} />
                </Section>
              </>
            )}

            {form.route === 'personal' && (
              <>
                <Section title="Your Ideal Auto Lifestyle">
                  <Textarea value={form.lifestyleVision} onChange={e => set('lifestyleVision', e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white min-h-[100px]" placeholder="Describe how you want to move through the world" />
                </Section>
                <Section title="Priority Goals">
                  <CheckboxGroup selected={form.priorityGoals} onToggle={v => toggleArray('priorityGoals', v)} options={[
                    'Reduce monthly vehicle cost', 'Upgrade to a better vehicle', 'Stop driving myself',
                    'Build credit for better vehicle access', 'Access luxury without ownership',
                    'Build a personal brand image', 'Get a personal / concierge driver', 'Manage multiple vehicles better',
                  ]} />
                </Section>
                <Section title="Vehicle Preferences">
                  <Field label="Dream vehicle or type" value={form.dreamVehicle} onChange={v => set('dreamVehicle', v)} />
                  <SelectField label="Acquisition method" value={form.acquisitionMethod} onChange={v => set('acquisitionMethod', v)}
                    options={['Purchase outright', 'Finance / auto loan', 'Lease', 'Subscription / access model', 'Chauffeured — no ownership', 'Not sure yet']} />
                  <Field label="Monthly budget for vehicle/transport" value={form.monthlyBudget} onChange={v => set('monthlyBudget', v)} />
                </Section>
              </>
            )}

            {form.route === 'corporate' && (
              <>
                <Section title="Transportation Needs">
                  <CheckboxGroup selected={form.transportNeeds} onToggle={v => toggleArray('transportNeeds', v)} options={[
                    'Executive / VIP transport', 'Airport transfer program', 'Event & conference transport',
                    'Employee commute / shuttle', 'Client entertainment transport', 'Multi-vehicle fleet coordination',
                    'Travel policy design', 'Cost reduction / optimization',
                  ]} />
                </Section>
                <Section title="Upcoming Events">
                  <Field label="Events requiring transport" value={form.upcomingEvents} onChange={v => set('upcomingEvents', v)} />
                  <Field label="Estimated passengers / vehicles" value={form.passengerCount} onChange={v => set('passengerCount', v)} />
                  <Field label="Event dates / frequency" value={form.eventDates} onChange={v => set('eventDates', v)} />
                  <Field label="Budget per event or monthly" value={form.eventBudget} onChange={v => set('eventBudget', v)} />
                </Section>
              </>
            )}
          </div>
        )}

        {/* Step 5 — Engagement */}
        {step === 5 && (
          <div className="space-y-8" data-testid="step-engagement">
            <div>
              <h2 className="text-lg font-bold mb-1">How Do We Work Together?</h2>
              <p className="text-sm text-gray-500">Timeline, support type, and how you'd like to engage with ACE.</p>
            </div>
            <Section title="Timeline">
              <RadioCards value={form.timeline} onChange={v => set('timeline', v)} options={[
                'Immediately — ready now', 'Within 1–3 months', '3–6 months',
                '6–12 months — still planning', 'No timeline — exploring',
              ]} />
            </Section>
            <Section title="Type of Support">
              <RadioCards value={form.supportType} onChange={v => set('supportType', v)} options={[
                'One-time strategy session / roadmap',
                'Ongoing monthly consulting retainer',
                'Deep mentorship / investor partnership',
                'ACE Membership — ongoing access & community',
                "Not sure yet — let's figure it out",
              ]} />
            </Section>
            <Section title="ACE Membership Interest">
              <RadioCards value={form.membershipInterest} onChange={v => set('membershipInterest', v)} options={[
                'Lobby ($97/mo) — Self-guided + community',
                'Condo ($397/mo) — 2 sessions/mo + messaging',
                'Penthouse ($1,200/mo) — Full concierge consulting',
                'Not sure yet',
              ]} />
            </Section>
            <Section title="Investment Range">
              <RadioCards value={form.investmentRange} onChange={v => set('investmentRange', v)} options={[
                'Under $1,000', '$1,000–$5,000', '$5,000–$25,000', '$25,000–$100,000', '$100,000+', 'Open / flexible',
              ]} />
            </Section>
            <Section title="Final Notes">
              <Textarea value={form.finalNotes} onChange={e => set('finalNotes', e.target.value)}
                className="bg-gray-900 border-gray-700 text-white min-h-[80px]" placeholder="Anything else you want us to know before your session?" />
            </Section>
            <p className="text-[11px] text-gray-600 leading-relaxed">By submitting, you confirm the information is accurate and you're interested in a paid Discovery Session. All information is strictly confidential.</p>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-800">
          {step > 1 ? (
            <Button onClick={() => setStep(s => s - 1)} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-900 text-xs" data-testid="btn-back">
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
            </Button>
          ) : <div />}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-600">Step {step} of 5</span>
            {step < 5 ? (
              <Button onClick={() => setStep(s => s + 1)} className="bg-red-600 hover:bg-red-700 text-xs" data-testid="btn-continue"
                disabled={step === 1 && (!form.fullName || !form.phone || !form.email) || step === 2 && !form.route}>
                Continue <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} className="bg-red-600 hover:bg-red-700 text-xs" disabled={submitting} data-testid="btn-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Submit Profile
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══ Reusable Sub-Components ═══ */

const Field = ({ label, value, onChange, type = 'text', testId }) => (
  <div>
    <label className="text-xs font-medium text-gray-400 mb-1.5 block">{label}</label>
    <Input type={type} value={value} onChange={e => onChange(e.target.value)}
      className="bg-gray-900 border-gray-700 text-white text-sm h-10"
      data-testid={testId} />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-xs font-medium text-gray-400 mb-1.5 block">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full h-10 px-3 rounded-md bg-gray-900 border border-gray-700 text-white text-sm">
      <option value="">Select...</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">{title}</p>
    <div className="grid sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

const RadioCards = ({ value, onChange, options }) => (
  <div className="sm:col-span-2 grid gap-2">
    {options.map(o => (
      <button key={o} onClick={() => onChange(o)} type="button"
        className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ${
          value === o ? 'border-red-600 bg-red-600/10 text-white' : 'border-gray-800 text-gray-400 hover:border-gray-700'
        }`}>
        <div className="flex items-center gap-3">
          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${value === o ? 'border-red-500' : 'border-gray-700'}`}>
            {value === o && <div className="w-2 h-2 rounded-full bg-red-500" />}
          </div>
          {o}
        </div>
      </button>
    ))}
  </div>
);

const CheckboxGroup = ({ selected, onToggle, options }) => (
  <div className="sm:col-span-2 grid sm:grid-cols-2 gap-2">
    {options.map(o => (
      <button key={o} onClick={() => onToggle(o)} type="button"
        className={`text-left px-4 py-3 rounded-lg border text-sm transition-all ${
          selected.includes(o) ? 'border-red-600 bg-red-600/10 text-white' : 'border-gray-800 text-gray-400 hover:border-gray-700'
        }`}>
        <div className="flex items-center gap-3">
          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selected.includes(o) ? 'bg-red-600 border-red-600' : 'border-gray-700'}`}>
            {selected.includes(o) && <CheckCircle className="h-3 w-3 text-white" />}
          </div>
          {o}
        </div>
      </button>
    ))}
  </div>
);

const RouteOption = ({ id, icon: Icon, label, sub, desc, color, selected, onClick }) => (
  <button onClick={onClick} type="button" data-testid={`route-${id}`}
    className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
      selected ? 'border-red-600 bg-red-600/5' : 'border-gray-800 hover:border-gray-700'
    }`}>
    <div className="flex items-start gap-4">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="flex-1">
        <p className="text-xs text-gray-600 font-bold tracking-wider uppercase">{sub}</p>
        <h3 className="text-base font-bold text-white">{label}</h3>
        <p className="text-sm text-gray-500 mt-1 italic">"{desc}"</p>
      </div>
      {selected && <CheckCircle className="h-5 w-5 text-red-500 shrink-0 mt-1" />}
    </div>
  </button>
);

export default ConsultingIntake;
